import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { MenuIcon } from '@/components/MenuIcon';
import { Modal } from '@/components/Modal';
import { useCanManage } from '@/lib/auth/permissions';
import { dbApi, mockList } from '@/lib/mock/db';
import { queryKeys } from '@/lib/queryKeys';
import { useSites, useWeeks } from '@/lib/queries';
import type { Site, Week } from '@/lib/types';
import { findCurrentWeek, getWeekLabel } from '@/lib/utils/week';
import { useMyProfile } from '@/pages/home/useMyProfile';
import { WidgetFrame } from '@/pages/home/widgets/WidgetFrame';
import { WeekSelect } from '@/pages/meetings/components/WeekSelect';

/**
 * 주단위 식단표. 예전 "오늘의 식단" 을 대신한다 — 오늘만 보면 내일 뭘 먹는지 매번 식당안내로 들어가야 했다.
 *
 * 본사 구내식당과 현장 구내식당을 <b>토글</b>로 가른다. 현장은 여럿이라 드롭박스로 고른다. 소속이
 * 현장이면 그 현장을, 아니면 본사를 처음 화면으로 삼는다 — 대개 자기 소속의 식단을 보기 때문이다.
 *
 * 식단은 본사·현장 모두 `menuText` 한 칸에 월~일 7줄로 들어 있다(식당안내 화면과 같은 형식).
 * 한 줄이 하루이고, 한 줄 안의 반찬은 `/` 나 `,` 로 나뉜다.
 *
 * <h2>여기가 <b>유일한 입구</b>다 (사용자 지시 2026-09-18)</h2>
 * 식당안내는 사이드바에서 내려왔다. 그래서 이 위젯이 조회만 하면 <b>아무도 식단을 못 넣는다</b> —
 * 머리에 연필을 달아 그 자리에서 주차별로 입력하고 저장한다. 권한은 화면과 같은 키
 * (`news_dining`) 이고, 없는 사람에게는 연필이 서지 않는다.
 *
 * <p>화면(`/news/dining`) 자체는 살아 있다 — 모바일 앱이 그 주소를 쓰고, 현장 장부식당(식당명·
 * 주소·가격)은 위젯에 담을 자료가 아니다.
 */

const WEEKDAY_LABELS = ['월', '화', '수', '목', '금', '토', '일'];

type Scope = 'hq' | 'site';

export function WeeklyMenuWidget() {
  const { primaryScope, locationName } = useMyProfile();
  const isSiteMember = primaryScope?.scopeType === 'site';
  // 메뉴 키를 줘야 서버가 준 표를 본다. 키 없이 부르면 관리자만 true 라 담당자의 수정 권한이 죽는다.
  const canManage = useCanManage('news_dining');

  const weeks = useWeeks();
  const sites = useSites();
  const hqMenus = useQuery({ queryKey: queryKeys.hqCafeteriaMenus, queryFn: () => mockList('hqCafeteriaMenus') });
  const siteMenus = useQuery({ queryKey: queryKeys.siteCafeteriaMenus, queryFn: () => mockList('siteCafeteriaMenus') });

  const activeSites = useMemo(
    () => (sites.data ?? []).filter((site) => site.active).sort((a, b) => a.sortOrder - b.sortOrder),
    [sites.data],
  );

  // 소속이 현장이면 그 현장으로 시작한다. 못 맞추면 첫 현장으로.
  const [scope, setScope] = useState<Scope>(isSiteMember ? 'site' : 'hq');
  const [siteId, setSiteId] = useState<string>('');
  const [editing, setEditing] = useState(false);

  const resolvedSiteId = siteId || activeSites.find((site) => site.name === locationName)?.id || activeSites[0]?.id || '';

  const currentWeek = findCurrentWeek(weeks.data ?? []);
  const weekStart = currentWeek?.startDate ?? null;

  const menuText =
    scope === 'hq'
      ? (hqMenus.data ?? []).find((menu) => menu.weekStart === weekStart)?.menuText ?? ''
      : (siteMenus.data ?? []).find((menu) => menu.siteId === resolvedSiteId && menu.weekStart === weekStart)?.menuText ?? '';

  const days = useMemo(() => buildDays(weekStart, menuText), [weekStart, menuText]);
  const todayIndex = weekStart ? dayIndexOf(new Date()) : -1;
  const hasAny = days.some((day) => day.dishes.length > 0);

  const pending = weeks.isPending || (scope === 'hq' ? hqMenus.isPending : siteMenus.isPending || sites.isPending);

  return (
    /*
      입력창은 <b>틀 밖</b>에 있다. {@link WidgetFrame} 은 로딩·오류·빈 목록일 때 본문을 그리지
      않는데, 「이번 주 식단이 없다」가 바로 연필을 누르는 순간이다 — 안에 두면 <b>넣을 것이 없을
      때만 창이 안 열린다.</b> 창 자체는 포털로 몸통에 붙으므로 자리를 차지하지 않는다.
    */
    <>
      <WidgetFrame
      title="주단위 식단표"
      icon="food"
      pending={pending}
      error={hqMenus.error ?? siteMenus.error}
      empty={!currentWeek}
      emptyText="이번 주 식단이 아직 등록되지 않았습니다."
      headerRight={
        <>
          <ScopeToggle scope={scope} onChange={setScope} />
          {canManage && (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="btn-ghost p-1"
              aria-label="식단 입력"
              title="식단 입력"
              style={{ color: 'var(--text-muted)' }}
            >
              <MenuIcon name="pencil" size={14} />
            </button>
          )}
        </>
      }
    >
      {scope === 'site' && (
        <select
          className="input-field input-xs mb-2"
          value={resolvedSiteId}
          onChange={(event) => setSiteId(event.target.value)}
          aria-label="현장 선택"
        >
          {activeSites.length === 0 ? (
            <option value="">등록된 현장이 없습니다</option>
          ) : (
            activeSites.map((site) => (
              <option key={site.id} value={site.id}>
                {site.name}
              </option>
            ))
          )}
        </select>
      )}

      {!hasAny ? (
        <p className="flex items-center justify-center text-center px-2 text-ui-sm" style={{ minHeight: '96px', color: 'var(--text-muted)' }}>
          이번 주 식단이 등록되지 않았습니다.
        </p>
      ) : (
        <div className="space-y-0.5">
          {days.map((day, index) => {
            const isToday = index === todayIndex;

            return (
              <div
                key={day.label}
                className="flex items-start gap-2 rounded-md px-1.5 py-1"
                style={{ background: isToday ? 'var(--accent-bg)' : 'transparent' }}
              >
                <span
                  className="flex-shrink-0 text-center text-ui-xs"
                  style={{
                    width: '34px',
                    fontWeight: isToday ? 700 : 500,
                    color: isToday ? 'var(--accent)' : 'var(--text-muted)',
                    lineHeight: 1.5,
                  }}
                >
                  {isToday ? '오늘' : day.label}
                  <span className="block text-ui-2xs" style={{ fontWeight: 400, color: 'var(--text-muted)' }}>
                    {day.date}
                  </span>
                </span>
                <span className="min-w-0 flex-1 text-ui-sm" style={{ color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                  {day.dishes.length > 0 ? day.dishes.join(' · ') : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                </span>
              </div>
            );
          })}
        </div>
      )}
      </WidgetFrame>

      {editing && (
        <MenuEditorModal
          scope={scope}
          sites={activeSites}
          initialSiteId={resolvedSiteId}
          weeks={weeks.data ?? []}
          initialWeekId={currentWeek?.id ?? null}
          onClose={() => setEditing(false)}
        />
      )}
    </>
  );
}

function ScopeToggle({ scope, onChange }: { scope: Scope; onChange: (next: Scope) => void }) {
  return (
    <span className="inline-flex rounded-lg overflow-hidden" style={{ border: '1px solid var(--border)' }}>
      {(['hq', 'site'] as const).map((value) => (
        <button
          key={value}
          onClick={() => onChange(value)}
          aria-pressed={scope === value}
          className="text-ui-xs"
          style={{
            padding: '3px 9px',
            fontWeight: scope === value ? 700 : 400,
            background: scope === value ? 'var(--accent-bg)' : 'white',
            color: scope === value ? 'var(--accent)' : 'var(--text-muted)',
          }}
        >
          {value === 'hq' ? '본사' : '현장'}
        </button>
      ))}
    </span>
  );
}

/**
 * 연필이 여는 입력창 — <b>주차를 고르고 일곱 칸을 채운다.</b>
 *
 * <h2>왜 위젯이 아니라 창 안에서 고치나</h2>
 * 위젯 한 칸은 반 칸(sm)이 기본이라 일곱 줄을 <b>고칠 수 있는 크기</b>로 세우지 못한다. 그리고
 * 고치는 일은 한 주에 한 번이고 보는 일은 매일이다 — 매일 쓰는 얼굴을 한 번 쓰는 일에 맞출 수 없다.
 *
 * <h2>입력 상태를 여기서 든다</h2>
 * 위젯 쪽에 두면 한 글자마다 목록·토글·쿼리가 함께 다시 그려진다(그리고 그때 입력이 튄다).
 * 이 창이 자기 칸만 들고 있으면 바깥은 저장할 때 한 번만 움직인다.
 *
 * <h2>지난 주차도 고칠 수 있다</h2>
 * {@link WeekSelect} 를 그대로 쓴다 — 식당안내 화면과 <b>같은 상자</b>다. 기본은 이번 주지만,
 * 다음 주 식단을 미리 넣거나 지난 주 오타를 고치는 일이 실제로 있다.
 */
function MenuEditorModal({
  scope,
  sites,
  initialSiteId,
  weeks,
  initialWeekId,
  onClose,
}: {
  scope: Scope;
  sites: Site[];
  initialSiteId: string;
  weeks: Week[];
  initialWeekId: string | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();

  const sortedWeeks = useMemo(
    () => [...weeks].sort((a, b) => b.startDate.localeCompare(a.startDate)),
    [weeks],
  );

  const [weekId, setWeekId] = useState(initialWeekId ?? sortedWeeks[0]?.id ?? '');
  const [siteId, setSiteId] = useState(initialSiteId);
  const [dayTexts, setDayTexts] = useState<string[]>(() => WEEKDAY_LABELS.map(() => ''));

  const week = sortedWeeks.find((candidate) => candidate.id === weekId) ?? null;

  // 고친 뒤 저장하지 않고 창을 닫았다가 다시 열면 그 사이 저장된 값이 보여야 한다 — 그래서
  // 목록을 캐시에서 읽지 않고 <b>창을 열 때·주차를 바꿀 때</b> 그 한 건만 직접 받는다.
  const saved = useQuery({
    queryKey: [...(scope === 'hq' ? queryKeys.hqCafeteriaMenus : queryKeys.siteCafeteriaMenus), 'editor', siteId, week?.startDate ?? ''],
    enabled: Boolean(week) && (scope === 'hq' || Boolean(siteId)),
    queryFn: async () => {
      if (scope === 'hq') {
        const rows = await mockList('hqCafeteriaMenus');
        return rows.find((row) => row.weekStart === week!.startDate)?.menuText ?? '';
      }
      const rows = await mockList('siteCafeteriaMenus');
      return rows.find((row) => row.siteId === siteId && row.weekStart === week!.startDate)?.menuText ?? '';
    },
  });

  // 저장된 값이 도착하면 칸을 채운다. 편집 중이던 내용은 이때 버려진다 — 식당안내 화면과 같다.
  useEffect(() => {
    const lines = (saved.data ?? '').split(/\r?\n/);
    setDayTexts(WEEKDAY_LABELS.map((_, index) => lines[index] ?? ''));
  }, [saved.data]);

  const weekDates = useMemo(() => {
    if (!week) return WEEKDAY_LABELS.map((label) => ({ label, date: '' }));

    const start = new Date(`${week.startDate}T00:00:00`);
    return WEEKDAY_LABELS.map((label, index) => {
      const current = new Date(start);
      current.setDate(start.getDate() + index);
      return { label, date: `${current.getMonth() + 1}/${current.getDate()}` };
    });
  }, [week]);

  const save = useMutation({
    mutationFn: () => {
      // 한 줄이 하루다. 칸 안에서 줄바꿈을 치면 뒤 요일이 한 칸씩 밀리므로 ` / ` 로 합친다.
      const menuText = WEEKDAY_LABELS.map((_, index) =>
        (dayTexts[index] ?? '')
          .split(/\r?\n/)
          .map((line) => line.trim())
          .filter(Boolean)
          .join(' / '),
      ).join('\n');

      // 주차(와 현장)가 행을 가리키는 키다. 서버가 있으면 갱신·없으면 생성하고, 비우면 지운다.
      return scope === 'hq'
        ? dbApi('/v1/cafeteria-menus', 'PUT', { weekStart: week!.startDate, menuText })
        : dbApi('/v1/site-cafeteria-menus', 'PUT', { siteId, weekStart: week!.startDate, menuText });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: scope === 'hq' ? queryKeys.hqCafeteriaMenus : queryKeys.siteCafeteriaMenus,
      });
      onClose();
    },
  });

  const blocked = !week || (scope === 'site' && !siteId);

  return (
    <Modal
      title={scope === 'hq' ? '본사 식단 입력' : '현장 식단 입력'}
      onClose={onClose}
      maxWidth="560px"
      closeDisabled={save.isPending}
      footer={
        <>
          <button onClick={onClose} className="btn-secondary" disabled={save.isPending}>
            취소
          </button>
          <button onClick={() => save.mutate()} className="btn-primary" disabled={blocked || save.isPending}>
            {save.isPending ? '저장 중...' : '저장'}
          </button>
        </>
      }
    >
      <div className="flex items-center gap-2 flex-wrap">
        <WeekSelect weeks={sortedWeeks} weekId={weekId} onChange={setWeekId} label={getWeekLabel} />
        {scope === 'site' && (
          <select
            className="input-field"
            style={{ width: 'auto', minWidth: 'min(180px, 45vw)' }}
            value={siteId}
            onChange={(event) => setSiteId(event.target.value)}
            aria-label="현장 선택"
          >
            {sites.length === 0 ? (
              <option value="">등록된 현장이 없습니다</option>
            ) : (
              sites.map((site) => (
                <option key={site.id} value={site.id}>
                  {site.name}
                </option>
              ))
            )}
          </select>
        )}
      </div>

      <div className="space-y-2">
        {weekDates.map((day, index) => (
          <div key={day.label} className="flex items-center gap-2">
            <span
              className="flex-shrink-0 text-center text-ui-xs"
              style={{ width: '46px', color: 'var(--text-muted)', fontWeight: 600 }}
            >
              {day.label}
              <span className="block text-ui-2xs" style={{ fontWeight: 400 }}>
                {day.date}
              </span>
            </span>
            <input
              className="input-field flex-1"
              value={dayTexts[index] ?? ''}
              onChange={(event) =>
                setDayTexts((prev) => prev.map((item, i) => (i === index ? event.target.value : item)))
              }
              placeholder={`${day.label}요일 식단`}
            />
          </div>
        ))}
      </div>

      {save.error && (
        <p className="text-ui-sm" style={{ color: 'var(--danger)' }}>
          저장하지 못했습니다. 잠시 뒤 다시 눌러 주세요.
        </p>
      )}
    </Modal>
  );
}

type Day = { label: string; date: string; dishes: string[] };

/** 주 시작일과 menuText(월~일 7줄)로 요일별 식단을 만든다. */
function buildDays(weekStart: string | null, menuText: string): Day[] {
  const lines = menuText.split(/\r?\n/);
  const start = weekStart ? new Date(`${weekStart}T00:00:00`) : null;

  return WEEKDAY_LABELS.map((label, index) => {
    const dishes = (lines[index] ?? '')
      .split(/[/,]/)
      .map((dish) => dish.trim())
      .filter(Boolean);

    let date = '';
    if (start) {
      const current = new Date(start);
      current.setDate(start.getDate() + index);
      date = `${current.getMonth() + 1}/${current.getDate()}`;
    }

    return { label, date, dishes };
  });
}

/** 월요일이 0, 일요일이 6. */
function dayIndexOf(date: Date): number {
  return (date.getDay() + 6) % 7;
}
